import { CommonModule } from '@angular/common';
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { AnalyticsService } from '@core/analytics.service';
import { AppConfigService } from '@core/appconfig.service';
import { AuthenticationService } from '@core/authentication.service';
import { BrowserService } from '@core/browser.service';
import { CacheService } from '@core/cache.service';
import { UserService } from '@core/user.service';
import { ButtonComponent } from '@shared/button/button.component';
import { supportLinks } from '@shared/enums/support-links';
import { PreloaderModule } from '@shared/preloader/preloader.module';
import { Html5Qrcode } from 'html5-qrcode/esm/html5-qrcode';
import { ToastrService } from 'ngx-toastr';
import { BehaviorSubject, Subscription } from 'rxjs';

/**
 *
 * Uses Html5Qrcode to process QR Codes.
 *
 * Shows QR Code image example with "Login with QR Code" button.
 *
 * Toggles view between showing QR Code scanner and the example with login button.
 */
@Component({
  selector: 'zbp-branded-qr-code-login',
  templateUrl: './branded-qr-code-login.component.html',
  styleUrls: ['./branded-qr-code-login.component.scss'],
  imports: [
    ButtonComponent,
    PreloaderModule,
    CommonModule,
  ]
})
export class BrandedQrCodeLoginComponent implements OnInit, OnDestroy {
  /** Used to track if QR Code Scanner is being used. Will update the UI to display the scanner. */
  @Input() isQrScannerRunning$: BehaviorSubject<boolean>;

  html5QrCode: any;
  showStudentQrCodeSpinner: boolean = false;
  supportLinks = supportLinks;

  private subscriptions: Subscription[] = [];

  constructor(
    private router: Router,
    private authService: AuthenticationService,
    private userService: UserService,
    private appConfig: AppConfigService,
    private cache: CacheService,
    public browserService: BrowserService,
    private analyticsService: AnalyticsService,
    private toastr: ToastrService) {
  }

  ngOnInit(): void {
    if (!this.isQrScannerRunning$) {
      this.isQrScannerRunning$ = new BehaviorSubject<boolean>(false);
    }
  }

  ngOnDestroy(): void {
    this.subscriptions?.forEach(sub => sub.unsubscribe());
  }

  /**
   * Sets up the UI to display the QR Code Scanner.
   *
   * Gets camera and then initializes the scanner.
   *
   * Error handling provided to reset the scanner if there is an error during scan.
   *
   * If there is an error loading cameras, toast is shown and login form will be reset.
   */
  prepQrCodeScan() {
    this.displayQrScanner(true);

    Html5Qrcode.getCameras().then((devices) => {
      if (devices && devices.length) {
        this.scanQRCode();
      }
    }).catch((err) => {
      console.error(err);
      this.toastr.error('Please ask for help to use the camera');
      this.displayQrScanner(false);
    });
  }

  /**
   * Called when QR code is being scanned, after cameras are setup by Html5Qrcode.
   *
   * Starts the scanner and provides success and error handling callbacks for scan result.
   *
   * Error callbacks will reset the scanner if there is an error.
   */
  scanQRCode() {
    const config = { fps: 10, qrbox: { width: 200, height: 200 } };
    this.html5QrCode = new Html5Qrcode('qrCodeScanner');
    this.html5QrCode.start({ facingMode: 'environment' }, config, (decodedText) => {
      this.parseAndLogin(decodedText);
      this.cancelQrCodeScan(false);
    }, (errorMessage) => {
      /* ignore insignificant media format chatter */
      if (errorMessage) {}
    })
      .catch((err) => {
        console.error(err);
        this.toastr.error(err);
      }).then(() => {
        // Remove spinner after Cameras and Scanner have been successfully setup.
        this.showStudentQrCodeSpinner = false;
      });
  }

  /**
   * Callback used by Html5Qrcode QR Code Scanner to login after code is scanned.
   *
   * Will reset QR Scanner if any errors happen during login.
   *
   * @param { string } decodedText Provided by Html5Qrcode during callback
   */
  parseAndLogin(decodedText: string) {
    this.showStudentQrCodeSpinner = true;
    let studentIdPosition = decodedText.indexOf('=');
    studentIdPosition++;
    let tokenPosition = decodedText.indexOf('&');
    const studentId = decodedText.substring(studentIdPosition, tokenPosition);
    tokenPosition += 7; // advance past parameter key
    const token = decodedText.substring(tokenPosition, decodedText.length);

    const loginQRCodeSub = this.userService.loginUserByQRCode(studentId, token).subscribe((res) => {
      if (res.success) {
        this.cache.coreApiToken = res.response.token;
        this.userService.setUserData(res.response);
        this.appConfig.setCustomUrls(res.response);
        this.authService.authStatus.next(true);
        this.cache.lastSchoolIdUsed = res.response.schools?.[0]?.abbreviatedSchoolId ?? '';
        this.cache.setStudentAsLastLoginPathUsed();
        this.analyticsService.setUserProperties();
        this.router.navigateByUrl('');
      } else {
        let error;
        if (res.messages) {
          error = res.messages;
        } else {
          error = 'An error has occurred. Please try again later.';
        }
        this.showStudentQrCodeSpinner = false;
        this.prepQrCodeScan();
        this.toastr.error(error);
      }
    });

    this.subscriptions.push(loginQRCodeSub);
  }

  /**
   * Usage:
   *   - Called by Cancel button on UI.
   *   - Callback used by QR Code Scanner that is called after scan completed.
   *
   * @param { boolean } backToForm If the login form should be shown.
   * Scanner will navigate after logging in and does not require the form being reset.
   */
  cancelQrCodeScan(backToForm: boolean) {
    this.html5QrCode?.stop().then(() => {
      this.html5QrCode.clear();

      // Toggle screen back to login form if User cancelled,
      if (backToForm) {
        this.displayQrScanner(false);
      }
    }).catch((err) => {
      this.displayQrScanner(false);
      console.error(err);
      this.toastr.error(err);
    });
  }

  /**
   *
   * Sets up form to display QR Code Scanner.
   *
   * If true, login form will be replaced with QR Scanner and dispatch that it it's QR Code Mode.
   *
   * If false, login form will be displayed with Login Form and "Login With QR Code" button,
   * clicking the 'Login With QR Code' loads the QR Scanner and replaces the form.
   *
   * @param { boolean } showQRScanner True if QR Scanner should start, False if regular login form should be shown.
   */
  private displayQrScanner(showQRScanner: boolean) {
    this.isQrScannerRunning$.next(showQRScanner);
    this.showStudentQrCodeSpinner = showQRScanner;
  }
}
